import { formatDistanceToNow } from "date-fns";

import { es } from "date-fns/locale";

import { useState } from "react";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Card } from "@/components/ui/card";

import { Checkbox } from "@/components/ui/checkbox";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DangerousDeleteDialog } from "../../../shared/components/DangerousDeleteDialog";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import {
  useAdminUsersQuery,
  useCreateAdminUser,
  useDeleteAdminUser,
  useResendInvitation,
  useUpdateAdminUser,
} from "../hooks/useAdmins";

import type {
  AdminUser,
  CreateAdminUserInput,
  UpdateAdminUserInput,
} from "../types";

import { ADMIN_ROLES } from "../types";

const AdminsPage = () => {
  const { data, isLoading, isFetching } = useAdminUsersQuery();

  const createMutation = useCreateAdminUser();

  const updateMutation = useUpdateAdminUser();

  const deleteMutation = useDeleteAdminUser();

  const resendMutation = useResendInvitation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state

  const [email, setEmail] = useState("");

  const [firstName, setFirstName] = useState("");

  const [lastName, setLastName] = useState("");

  const [phone, setPhone] = useState("");

  const [selectedRoles, setSelectedRoles] = useState<string[]>([
    ADMIN_ROLES.DEVELOPER,
  ]);

  const [formError, setFormError] = useState<string | null>(null);

  const admins = data?.data ?? [];

  const total = data?.total ?? 0;

  const isBusy =
    isLoading ||
    isFetching ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    resendMutation.isPending;

  const resetForm = () => {
    setEmail("");

    setFirstName("");

    setLastName("");

    setPhone("");

    setSelectedRoles([ADMIN_ROLES.DEVELOPER]);

    setFormError(null);

    setEditingAdmin(null);
  };

  const openCreateModal = () => {
    resetForm();

    setIsModalOpen(true);
  };

  const openEditModal = (admin: AdminUser) => {
    setEditingAdmin(admin);

    setEmail(admin.email ?? "");

    setFirstName(admin.firstName);

    setLastName(admin.lastName);

    setPhone(admin.phone ?? "");

    setSelectedRoles(
      admin.roles.length > 0 ? admin.roles : [ADMIN_ROLES.DEVELOPER],
    );

    setFormError(null);

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);

    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFormError(null);

    if (!email.trim()) {
      setFormError("El email es requerido");

      return;
    }

    if (!firstName.trim()) {
      setFormError("El nombre es requerido");

      return;
    }

    if (!lastName.trim()) {
      setFormError("El apellido es requerido");

      return;
    }

    try {
      if (editingAdmin) {
        const input: UpdateAdminUserInput = {
          email: email.trim(),

          firstName: firstName.trim(),

          lastName: lastName.trim(),

          phone: phone.trim() || undefined,

          roles: selectedRoles,
        };

        await updateMutation.mutateAsync({ id: editingAdmin.id, input });
      } else {
        const input: CreateAdminUserInput = {
          email: email.trim(),

          firstName: firstName.trim(),

          lastName: lastName.trim(),

          phone: phone.trim() || undefined,

          roles: selectedRoles,
        };

        await createMutation.mutateAsync(input);
      }

      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Error al guardar";

      setFormError(message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);

      setDeleteConfirmId(null);
    } catch {
      // Surface to the user via toast/UI; never log error details — they can
      // include tokens or IDs that leak through DevTools if the session is
      // compromised. The mutation hook already exposes `error` state for UX.
    }
  };

  const handleResend = async (id: string) => {
    try {
      await resendMutation.mutateAsync(id);
    } catch {
      // See note above — silent on purpose.
    }
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">
            Pendiente
          </Badge>
        );

      case "active":
        return (
          <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
            Activo
          </Badge>
        );

      case "disabled":
        return (
          <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30">
            Deshabilitado
          </Badge>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Administradores"
        subtitle="Gestiona los usuarios con acceso al backoffice"
        actions={
          <Button
            onClick={openCreateModal}
            className="bg-purple-600 text-white hover:bg-purple-700"
          >
            Invitar administrador
          </Button>
        }
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">Nombre</TableHead>
              <TableHead className="text-neutral-400">Email</TableHead>
              <TableHead className="text-neutral-400">Estado</TableHead>
              <TableHead className="text-neutral-400">Roles</TableHead>
              <TableHead className="text-neutral-400">Último acceso</TableHead>
              <TableHead className="text-right text-neutral-400">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.map((admin) => (
              <TableRow
                key={admin.id}
                className="border-neutral-800 hover:bg-neutral-800/50"
              >
                <TableCell className="font-medium text-neutral-100">
                  {admin.firstName} {admin.lastName}
                </TableCell>
                <TableCell className="text-neutral-400">
                  {admin.email ?? "-"}
                </TableCell>
                <TableCell>{getStatusBadge(admin.status)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {admin.roles.length === 0 ? (
                      <span className="text-xs text-neutral-500">
                        Sin roles
                      </span>
                    ) : (
                      admin.roles.map((role) => {
                        const isAdminRole = role === ADMIN_ROLES.ADMIN;

                        const isFounderRole = role === ADMIN_ROLES.FOUNDER;

                        const className = isAdminRole
                          ? "border-purple-500/50 text-purple-400"
                          : isFounderRole
                            ? "border-amber-500/50 text-amber-400"
                            : "border-neutral-600 text-neutral-300";

                        const label = isAdminRole
                          ? "Admin"
                          : isFounderRole
                            ? "Founder"
                            : "Developer";

                        return (
                          <Badge
                            key={role}
                            variant="outline"
                            className={className}
                          >
                            {label}
                          </Badge>
                        );
                      })
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-neutral-300">
                  {admin.lastLoginAt
                    ? formatDistanceToNow(new Date(admin.lastLoginAt), {
                        addSuffix: true,

                        locale: es,
                      })
                    : "Nunca"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {admin.status === "pending" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResend(admin.id)}
                        disabled={resendMutation.isPending}
                        className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/10"
                      >
                        Reenviar
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(admin)}
                      className="border-neutral-700 text-neutral-300 hover:border-purple-500 hover:text-purple-400"
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteConfirmId(admin.id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      Eliminar
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isBusy && <LoadingOverlay />}

        <DangerousDeleteDialog
          open={deleteConfirmId !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteConfirmId(null);
          }}
          title="Eliminar administrador"
          description={`Vas a eliminar permanentemente al administrador ${
            admins.find((a) => a.id === deleteConfirmId)?.email ?? ""
          }. Esta acción no se puede deshacer.`}
          loading={deleteMutation.isPending}
          onConfirm={() => {
            if (deleteConfirmId) void handleDelete(deleteConfirmId);
          }}
        />

        {!isLoading && admins.length === 0 && (
          <EmptyState
            title="Sin administradores"
            description="No hay administradores registrados."
            className="m-6"
          />
        )}

        <div className="border-t border-neutral-800 px-4 py-3 text-xs text-neutral-400">
          Total: {total} administrador{total !== 1 ? "es" : ""}
        </div>
      </Card>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="border-neutral-800 bg-neutral-900 text-neutral-100">
          <DialogHeader>
            <DialogTitle>
              {editingAdmin ? "Editar administrador" : "Invitar administrador"}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {editingAdmin
                ? "Modifica los datos del administrador."
                : "Envía una invitación por email."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email" className="text-neutral-300">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                disabled={!!editingAdmin}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-100"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="firstName" className="text-neutral-300">
                  Nombre *
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Juan"
                  required
                  className="border-neutral-700 bg-neutral-800/50 text-neutral-100"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="lastName" className="text-neutral-300">
                  Apellido *
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Pérez"
                  required
                  className="border-neutral-700 bg-neutral-800/50 text-neutral-100"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone" className="text-neutral-300">
                Teléfono
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+54 9 11 1234 5678"
                className="border-neutral-700 bg-neutral-800/50 text-neutral-100"
              />
            </div>

            <div className="grid gap-2">
              <Label className="text-neutral-300">Roles</Label>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="role-admin"
                    checked={selectedRoles.includes(ADMIN_ROLES.ADMIN)}
                    onCheckedChange={() => toggleRole(ADMIN_ROLES.ADMIN)}
                  />
                  <Label
                    htmlFor="role-admin"
                    className="cursor-pointer text-neutral-200"
                  >
                    Admin
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="role-developer"
                    checked={selectedRoles.includes(ADMIN_ROLES.DEVELOPER)}
                    onCheckedChange={() => toggleRole(ADMIN_ROLES.DEVELOPER)}
                  />
                  <Label
                    htmlFor="role-developer"
                    className="cursor-pointer text-neutral-200"
                  >
                    Developer
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="role-founder"
                    checked={selectedRoles.includes(ADMIN_ROLES.FOUNDER)}
                    onCheckedChange={() => toggleRole(ADMIN_ROLES.FOUNDER)}
                  />
                  <Label
                    htmlFor="role-founder"
                    className="cursor-pointer text-neutral-200"
                  >
                    Founder
                  </Label>
                </div>
              </div>
              <p className="text-xs text-neutral-500">
                Solo los usuarios con rol Admin pueden gestionar otros
                administradores.
              </p>
            </div>

            {!editingAdmin && (
              <p className="rounded-md bg-neutral-800/50 p-3 text-xs text-neutral-400">
                Se enviará un email de invitación con un enlace para que el
                usuario defina su contraseña.
              </p>
            )}

            {formError && <p className="text-sm text-red-400">{formError}</p>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="border-neutral-700 text-neutral-300"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="bg-purple-600 text-white hover:bg-purple-700"
              >
                {editingAdmin ? "Guardar cambios" : "Enviar invitación"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminsPage;
